import React, { createContext } from 'react';
import { request } from '../api-client';
import { 
  getStoredUser, 
  getAlbumFavorites, 
  getArtistFavorites, 
  getMusicFavorites,
  getContentCreatorFavorites, 
  getContentCreationFavorites,
  setAlbumFavorites,
  setArtistFavorites,
  setMusicFavorites,
  setContentCreatorFavorites,
  setContentCreationFavorites,
  getMainTranslation
} from '../api-client/core/authentication/utils';
import analyticsInstance from "../analytics/analyticsInstance";

const FavoritesContext = createContext();

export class FavoritesProvider extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      favorites: {
        albums: [],
        artists: [],
        musics: [],
        contentCreators: [],
        contentCreations: [],
        playlists: [],
      },
      loading: false,
    };
  }

  componentDidMount() {
    this.loadAllFavorites();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.channel !== this.props.channel) {
      this.loadAllFavorites();
    }
  }

  loadAllFavorites = async () => {
    const { channel } = this.props;
    if (!getStoredUser() || !channel) return;
    
    this.setState({ loading: true });
    try {
      const [albums, artists, musics, contentCreators, contentCreations] = await Promise.all([
        channel.type !== "content" ? request.albumChannelFavorite.getAll() : Promise.resolve([]),
        channel.type !== "content" ? request.artistChannelFavorite.getAll() : Promise.resolve([]),
        channel.type !== "content" ? request.musicChannelFavorite.getAll() : Promise.resolve([]),
        channel.type !== "music" ? request.contentCreatorChannelFavorite.getAll() : Promise.resolve([]),
        channel.type !== "music" ? request.contentCreationChannelFavorite.getAll() : Promise.resolve([]),
      ]);
      
      this.setState({
        favorites: {
          albums: albums || [],
          artists: artists || [],
          musics: musics || [],
          contentCreators: contentCreators || [],
          contentCreations: contentCreations || [],
          playlists: [], // Will be loaded separately if needed
        }
      });

      // Update global state for compatibility
      setAlbumFavorites(albums || []);
      setArtistFavorites(artists || []);
      setMusicFavorites(musics || []);
      setContentCreatorFavorites(contentCreators || []);
      setContentCreationFavorites(contentCreations || []);
      
    } catch (error) {
      console.error('Error loading favorites:', error);
    } finally {
      this.setState({ loading: false });
    }
  };

  addToFavorites = async (id, type = 'video_content_creation') => {
    if (!getStoredUser()) return false;
    
    console.log('addToFavorites', id, type)
    try {
      let apiCall;
      let favoriteKey;
      
      switch (type) {
        case 'album':
          apiCall = request.albumChannelFavorite.add(id);
          favoriteKey = 'albums';
          break;
        case 'artist':
          apiCall = request.artistChannelFavorite.add(id);
          favoriteKey = 'artists';
          break;
        case 'music':
          apiCall = request.musicChannelFavorite.add(id);
          favoriteKey = 'musics';
          break;
        case 'content_creator':
          apiCall = request.contentCreatorChannelFavorite.add(id);
          favoriteKey = 'contentCreators';
          break;
        case 'video_content_creation':
          apiCall = request.contentCreationChannelFavorite.add(id);
          favoriteKey = 'contentCreations';
          break;
        case 'playlist':
          apiCall = request.playlistChannelFavorite.add(id);
          favoriteKey = 'playlists';
          break;
        default:
          return false;
      }
      
      await apiCall;
      
      this.setState(prevState => ({
        favorites: {
          ...prevState.favorites,
          [favoriteKey]: [...prevState.favorites[favoriteKey], id]
        }
      }));
      
      // Update global state for compatibility
      this.updateGlobalState(type, id, 'add');
      
      return true;
    } catch (error) {
      console.error('Error adding to favorites:', error);
      return false;
    }
  };

  removeFromFavorites = async (id, type = 'video_content_creation') => {
    if (!getStoredUser()) return false;
    
    try {
      let apiCall;
      let favoriteKey;
      
      switch (type) {
        case 'album':
          apiCall = request.albumChannelFavorite.remove(id);
          favoriteKey = 'albums';
          break;
        case 'artist':
          apiCall = request.artistChannelFavorite.remove(id);
          favoriteKey = 'artists';
          break;
        case 'music':
          apiCall = request.musicChannelFavorite.remove(id);
          favoriteKey = 'musics';
          break;
        case 'content_creator':
          apiCall = request.contentCreatorChannelFavorite.remove(id);
          favoriteKey = 'contentCreators';
          break;
        case 'video_content_creation':
          apiCall = request.contentCreationChannelFavorite.remove(id);
          favoriteKey = 'contentCreations';
          break;
        case 'playlist':
          apiCall = request.playlistChannelFavorite.remove(id);
          favoriteKey = 'playlists';
          break;
        default:
          return false;
      }
      
      await apiCall;
      
      this.setState(prevState => ({
        favorites: {
          ...prevState.favorites,
          [favoriteKey]: prevState.favorites[favoriteKey].filter(favId => favId !== id)
        }
      }));
      
      // Update global state for compatibility
      this.updateGlobalState(type, id, 'remove');
      
      return true;
    } catch (error) {
      console.error('Error removing from favorites:', error);
      return false;
    }
  };

  updateGlobalState = (type, id, action) => {
    switch (type) {
      case 'album':
        const albums = getAlbumFavorites();
        if (action === 'add') {
          albums.push(id);
        } else {
          const index = albums.indexOf(id);
          if (index > -1) albums.splice(index, 1);
        }
        setAlbumFavorites(albums);
        break;
      case 'artist':
        const artists = getArtistFavorites();
        if (action === 'add') {
          artists.push(id);
        } else {
          const index = artists.indexOf(id);
          if (index > -1) artists.splice(index, 1);
        }
        setArtistFavorites(artists);
        break;
      case 'music':
        const musics = getMusicFavorites();
        if (action === 'add') {
          musics.push(id);
        } else {
          const index = musics.indexOf(id);
          if (index > -1) musics.splice(index, 1);
        }
        setMusicFavorites(musics);
        break;
      case 'content_creator':
        const contentCreators = getContentCreatorFavorites();
        if (action === 'add') {
          contentCreators.push(id);
        } else {
          const index = contentCreators.indexOf(id);
          if (index > -1) contentCreators.splice(index, 1);
        }
        setContentCreatorFavorites(contentCreators);
        break;
      case 'video_content_creation':
        const contentCreations = getContentCreationFavorites();
        if (action === 'add') {
          contentCreations.push(id);
        } else {
          const index = contentCreations.indexOf(id);
          if (index > -1) contentCreations.splice(index, 1);
        }
        setContentCreationFavorites(contentCreations);
        break;
    }
  };

  toggleFavorite = async (item, type = 'video_content_creation') => {
    const isCurrentlyFavorite = this.isFavorite(item.id, type);

    analyticsInstance.favorite({
      name: getMainTranslation(item, "title"),
      type: type === "content_creator" ? "Content" : type === "video_content_creation" ? "Material" : type,
      content: !isCurrentlyFavorite ? "Add" : "Remove"
    })
    
    if (isCurrentlyFavorite) {
      return await this.removeFromFavorites(item.id, type);
    } else {
      return await this.addToFavorites(item.id, type);
    }
  };

  isFavorite = (id, type = 'video_content_creation') => {
    const { favorites } = this.state;
    switch (type) {
      case 'album':
        return favorites.albums.includes(id);
      case 'artist':
        return favorites.artists.includes(id);
      case 'music':
        return favorites.musics.includes(id);
      case 'content_creator':
        return favorites.contentCreators.includes(id);
      case 'video_content_creation':
        return favorites.contentCreations.includes(id);
      case 'playlist':
        return favorites.playlists.includes(id);
      default:
        return false;
    }
  };

  refreshFavorites = () => {
    this.loadAllFavorites();
  };

  getFavorites = (type) => {
    const { favorites } = this.state;
    switch (type) {
      case 'album': return favorites.albums;
      case 'artist': return favorites.artists;
      case 'music': return favorites.musics;
      case 'content_creator': return favorites.contentCreators;
      case 'video_content_creation': return favorites.contentCreations;
      case 'playlist': return favorites.playlists;
      default: return [];
    }
  };

  render() {
    const value = {
      favorites: this.state.favorites,
      loading: this.state.loading,
      addToFavorites: this.addToFavorites,
      removeFromFavorites: this.removeFromFavorites,
      toggleFavorite: this.toggleFavorite,
      isFavorite: this.isFavorite,
      refreshFavorites: this.refreshFavorites,
      getFavorites: this.getFavorites,
    };

    return (
      <FavoritesContext.Provider value={value}>
        {this.props.children}
      </FavoritesContext.Provider>
    );
  }
}

export default FavoritesContext;
